import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, message } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { theme } from '../../../theme';

const { Option } = Select;

interface MotorModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  motor?: {
    id: number;
    descripcion: string;
    id_sector: number;
    id_equipo: number;
    alta?: number;
  } | null;
}

interface SectorData {
  id: number;
  descripcion: string;
  alta: number;
}

interface EquipoData {
  id: number;
  sector: {
    id: number;
    descripcion: string;
  };
  descripcion: string;
  alta: number;
}

const MotorModal: React.FC<MotorModalProps> = ({ visible, onClose, onSuccess, motor }) => {
  const { user, getAccessTokenSilently } = useAuth0();
  const empresa = user?.id_empresa;
  const sucursal = user?.id_sucursal;
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [sectores, setSectores] = useState<SectorData[]>([]);
  const [equipos, setEquipos] = useState<EquipoData[]>([]);
  const [loadingSectores, setLoadingSectores] = useState(false);
  const [loadingEquipos, setLoadingEquipos] = useState(false);

  const isEditing = !!motor;

  // Cargar sectores
  const fetchSectores = async () => {
    setLoadingSectores(true);
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/listar_sectores/${empresa}/${sucursal}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) throw new Error('Error al cargar sectores');

      const data = await response.json();
      const activos = data.filter((item: SectorData) => item.alta === 1);
      setSectores(activos);
    } catch (error) {
      message.error('Error al cargar los sectores');
    } finally {
      setLoadingSectores(false);
    }
  };

  // Cargar equipos por sector
  const fetchEquipos = async (sectorId: number) => {
    setLoadingEquipos(true);
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/listar_equipos_por_sector/${empresa}/${sucursal}/${sectorId}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) throw new Error('Error al cargar equipos');

      const data = await response.json();
      const activos = data.filter((item: EquipoData) => 
        item.alta === 1 && item.sector.id === sectorId
      );
      setEquipos(activos);
    } catch (error) {
      message.error('Error al cargar los equipos');
    } finally {
      setLoadingEquipos(false);
    }
  };

  // Cargar datos si es edición
  useEffect(() => {
    if (visible) {
      fetchSectores();
      if (motor) {
        form.setFieldsValue({
          descripcion: motor.descripcion,
          id_sector: motor.id_sector,
          id_equipo: motor.id_equipo,
          alta: motor.alta !== undefined ? motor.alta : 1
        });
        fetchEquipos(motor.id_sector);
      } else {
        form.resetFields();
        setEquipos([]);
      }
    }
  }, [visible, motor, form]);

  const handleSectorChange = (sectorId: number) => {
    form.setFieldValue('id_equipo', undefined); // Reset equipo al cambiar sector
    fetchEquipos(sectorId);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const accessToken = await getAccessTokenSilently();
      const url = isEditing
        ? `${process.env.REACT_APP_INSUMOS_URL}/editar_motores/${empresa}/${sucursal}/${motor!.id}`
        : `${process.env.REACT_APP_INSUMOS_URL}/crear_motores`;

      const response = await fetch(url, {
        method: 'POST', // Siempre POST (tanto para crear como editar)
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          id_sector: values.id_sector,
          id_equipo: values.id_equipo,
          descripcion: values.descripcion,
          alta: values.alta,
          ...(isEditing ? {} : {
            id_empresa: empresa,
            id_sucursal: sucursal,
          })
        }),
      });

      if (!response.ok) {
        throw new Error('Error en la respuesta del servidor');
      }

      message.success(`Motor ${isEditing ? 'actualizado' : 'creado'} correctamente`);
      form.resetFields();
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error:', error);
      message.error(`Error al ${isEditing ? 'actualizar' : 'crear'} el motor`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <span style={{ 
          color: theme.colors.primaryDark, 
          fontSize: theme.fontSizes.large,
          fontWeight: 600
        }}>
          {isEditing ? 'Editar Motor' : 'Nuevo Motor'}
        </span>
      }
      open={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      okText={isEditing ? 'Actualizar' : 'Crear'}
      cancelText="Cancelar"
      okButtonProps={{
        style: {
          backgroundColor: theme.colors.secondary,
          borderColor: theme.colors.secondary,
          fontWeight: 500
        }
      }}
      cancelButtonProps={{
        style: {
          fontWeight: 500
        }
      }}
    >
      <Form
        form={form}
        layout="vertical"
        style={{ marginTop: '20px' }}
        initialValues={{
          alta: 1 // Activo por defecto para nuevos motores
        }}
      >
        <Form.Item 
          name="descripcion" 
          label={
            <span style={{ 
              color: theme.colors.textPrimary, 
              fontWeight: 600 
            }}>
              Descripción
            </span>
          }
          rules={[{ required: true, message: 'Por favor ingrese una descripción' }]}
        >
          <Input 
            maxLength={30} 
            placeholder="Ingrese la descripción del motor"
          />
        </Form.Item>

        <Form.Item 
          name="id_sector" 
          label={
            <span style={{ 
              color: theme.colors.textPrimary, 
              fontWeight: 600 
            }}>
              Sector
            </span>
          }
          rules={[{ required: true, message: 'Por favor seleccione un sector' }]}
        >
          <Select
            placeholder="Seleccione un sector"
            loading={loadingSectores}
            onChange={handleSectorChange}
          >
            {sectores.map((sector) => (
              <Option key={sector.id} value={sector.id}>
                {sector.descripcion}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item 
          name="id_equipo" 
          label={
            <span style={{ 
              color: theme.colors.textPrimary, 
              fontWeight: 600 
            }}>
              Equipo
            </span>
          }
          rules={[{ required: true, message: 'Por favor seleccione un equipo' }]}
        >
          <Select
            placeholder="Seleccione un equipo"
            loading={loadingEquipos}
            disabled={equipos.length === 0}
          >
            {equipos.map((equipo) => (
              <Option key={equipo.id} value={equipo.id}>
                {equipo.descripcion}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item 
          name="alta" 
          label={
            <span style={{ 
              color: theme.colors.textPrimary, 
              fontWeight: 600 
            }}>
              Estado
            </span>
          }
          rules={[{ required: true, message: 'Por favor seleccione el estado' }]}
        >
          <Select placeholder="Seleccione el estado">
            <Option value={1}>Activo</Option>
            <Option value={0}>Inactivo</Option>
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default MotorModal;

